// src/pages/RegisterPage.js
//
// Public registration screen
// ==========================
//
// PURPOSE
// -------
// User-facing registration page for the public/auth shell.
//
// IMPORTANT ROUTER NOTE
// ---------------------
// App uses BrowserRouter basename="/app".
// Therefore React Router paths here must remain INTERNAL:
//
// - "/"
// - "/register"
//
// Never prefix routes manually with "/app".
//
// PUBLIC i18n STRATEGY
// --------------------
// Register is a public page. The user is not authenticated yet and may not have
// a profile in hb_visits. Therefore this page must not depend on
// user.preferred_language.
//
// The language used here is the public display language:
// - initialized globally from browser/OS language,
// - changeable manually through a small dropdown,
// - not persisted to localStorage/sessionStorage.
//
// The special LoginPage-only "Profile language" mode is intentionally NOT shown
// on registration because a new user profile is not available yet.
//

import React, { useState, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../context/AuthContext"; // Import AuthContext
import PublicLanguageDropdown from "../components/PublicLanguageDropdown";
import { normalizeLanguage } from "../i18n";

import "../styles/FormStyles.css"; // Import shared CSS for styling

/* -------------------------------------------------------------------------- */
/* Error helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Convert backend / transport errors into human-readable registration messages.
 *
 * Backend-provided error messages are preserved as payload content. Frontend
 * fallbacks are translated in the auth namespace.
 */
function getRegisterErrorMessage(err, t) {
  const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
  if (serverMsg) return serverMsg;

  return t("register.errors.unexpected");
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const RegisterPage = () => {
  const { t, i18n } = useTranslation("auth");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState(""); // ✅ Store success message
  const [loading, setLoading] = useState(false);

  const { registerUser } = useContext(AuthContext); // Access function from AuthContext

  const currentLanguage = normalizeLanguage(
    i18n.resolvedLanguage || i18n.language
  );

  /**
   * Registration pages only expose concrete languages.
   *
   * Unlike LoginPage, there is no "Profile language" option here because a new
   * anonymous visitor does not yet have an authenticated profile from which the
   * application could read preferred_language.
   */
  const onPublicLanguageChange = useCallback(
    async (selection) => {
      const nextLanguage = selection?.language;
      if (!nextLanguage || nextLanguage === currentLanguage) return;
      await i18n.changeLanguage(nextLanguage);
    },
    [currentLanguage, i18n]
  );

  /**
   * Handles user registration.
   *
   * Important:
   * - the backend registration contract remains unchanged,
   * - we do not send preferred_language here in this batch,
   * - future backend support could later persist the current i18n language for
   *   newly registered users, but that would be a separate cross-layer change.
   */
  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    if (!firstName || !lastName) {
      setError(t("register.errors.firstAndLastNameRequired"));
      setLoading(false);
      return;
    }

    try {
      await registerUser(username, password, firstName, lastName);
      console.log("[RegisterPage] Registration successful.");
      setSuccessMessage(t("register.success.message"));
    } catch (err) {
      setError(getRegisterErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <PublicLanguageDropdown
          value={currentLanguage}
          onChange={onPublicLanguageChange}
        />

        <h1>{t("register.title")}</h1>

        <form className="form" onSubmit={handleRegister}>
          <label htmlFor="firstName" className="label">
            {t("register.fields.firstName")}
          </label>
          <input
            id="firstName"
            type="text"
            placeholder={t("register.placeholders.firstName")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="input"
            required
            autoComplete="given-name"
          />

          <label htmlFor="lastName" className="label">
            {t("register.fields.lastName")}
          </label>
          <input
            id="lastName"
            type="text"
            placeholder={t("register.placeholders.lastName")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="input"
            required
            autoComplete="family-name"
          />

          <label htmlFor="username" className="label">
            {t("register.fields.username")}
          </label>
          <input
            id="username"
            type="text"
            placeholder={t("register.placeholders.username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input"
            required
            autoComplete="username"
          />

          <label htmlFor="password" className="label">
            {t("register.fields.password")}
          </label>
          <input
            id="password"
            type="password"
            placeholder={t("register.placeholders.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            required
            autoComplete="new-password"
          />

          <button type="submit" className="button" disabled={loading}>
            {loading
              ? t("register.actions.registering")
              : t("register.actions.register")}
          </button>
        </form>

        {error && (
          <p className="error" role="alert" aria-live="polite">
            {error}
          </p>
        )}

        {/* ✅ Show success message with a "Go to Login" link */}
        {successMessage && (
          <p className="success" role="status" aria-live="polite">
            {successMessage} <br />
            {/*
              Login is mounted at "/" in App.js. We intentionally link to the
              internal router path, not to "/app", because BrowserRouter already
              applies basename="/app".
            */}
            <Link to="/" className="login-link">
              {t("register.success.loginLink")}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
